/**
 * NumericSliderUI - 结合数字输入框和滑块的复合控制组件
 * 左侧是数字输入框，右侧是滑块，两者双向同步
 * 仿照 numericInputUI 和 sliderUI 的实现
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface NumericSliderOptions extends ElementOptions {
  /** 最小值，null 表示不限制，0 表示有效的最小值 */
  min?: number | null;
  /** 最大值，null 表示不限制，0 表示有效的最大值 */
  max?: number | null;
  /** 步长 */
  step?: number;
  /** 精度（小数位数） */
  precision?: number;
  /** 初始值 */
  value?: number;
  /** 标签文本 */
  label?: string;
  /** 是否显示当前值 */
  showValue?: boolean;
  /** 值显示格式 */
  valueFormat?: (value: number) => string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否只读 */
  readonly?: boolean;
  /** 是否允许空值 */
  allowNull?: boolean;
  /** 标签样式类 */
  labelClass?: string;
  /** 控制容器样式类 */
  controlsContainerClass?: string;
  /** 输入框样式类 */
  inputClass?: string;
  /** 滑块样式类 */
  sliderClass?: string;
  /** 值显示区域的样式类 */
  valueClass?: string;
  /** 容器样式类 */
  containerClass?: string;
  /** 是否在输入时触发变化事件 */
  keyChange?: boolean;
  /** 是否在按Enter时失焦 */
  blurOnEnter?: boolean;
  /** 是否在按Escape时失焦 */
  blurOnEscape?: boolean;
  /** 占位符文本 */
  placeholder?: string;
  sliderContainerClass?: string;
}

export class NumericSliderUI {
  private ui: BootstrapUI;
  private container: HTMLElement;
  private inputElement: HTMLInputElement;
  private trackContainer: HTMLElement;
  private leftTrack: HTMLElement;
  private rightTrack: HTMLElement;
  private sliderElement: HTMLElement;
  private valueDisplayElement: HTMLElement | null = null;
  private options: NumericSliderOptions;
  private onChangeCallbacks: ((value: number | null) => void)[] = [];

  // 防止循环更新的标志
  private isUpdating: boolean = false;
  private previousValue: number = 0;
  
  // 拖拽相关状态
  private isDragging: boolean = false;
  private startX: number = 0;
  private startValue: number = 0;

  constructor(options: NumericSliderOptions = {}) {
    this.ui = bootstrapUI;
    this.options = {
      step: 0.1,
      precision: 2,
      value: 0,
      label: '数值滑块控制',
      showValue: true,
      valueFormat: (value: number) => value.toFixed(2),
      disabled: false,
      readonly: false,
      allowNull: false,
      labelClass: '',
      controlsContainerClass:'',
      inputClass: '',
      sliderClass: '',
      valueClass: '',
      containerClass: '',
      keyChange: false,
      blurOnEnter: true,
      blurOnEscape: true,
      sliderContainerClass: '',
      ...options
    };

    this.container = this.createElement();
    this.inputElement = this.container.querySelector('input[type="number"]') as HTMLInputElement;
    this.trackContainer = this.container.querySelector('.custom-slider-track') as HTMLElement;
    this.leftTrack = this.container.querySelector('.custom-slider-left-track') as HTMLElement;
    this.rightTrack = this.container.querySelector('.custom-slider-right-track') as HTMLElement;
    this.sliderElement = this.container.querySelector('.custom-slider-thumb') as HTMLElement;
    this.valueDisplayElement = this.container.querySelector('.numeric-slider-value') as HTMLElement;

    this.setupEventHandlers();
    this.updateSliderPosition(this.options.value || 0);
  }

  /**
   * 创建数值滑块组件元素
   * @returns 包含输入框和滑块的容器元素
   */
  private createElement(): HTMLElement {
    let labeli18nKey = '';
    if(this.options.i18nKey){
      labeli18nKey = this.options.i18nKey;
      this.options.i18nKey = '';
    }
    const container = this.ui.createElement({
      className: [
        'numeric-slider-container',
        ...(this.options.containerClass ? [this.options.containerClass] : []),
      ],
      ...this.options
    });

    // 创建标签
    if (this.options.label) {
      const label = this.ui.createElement({
        tag: 'label',
        className: ['form-label',
          ...(this.options.labelClass ? [this.options.labelClass] : []),
        ],
        textContent: this.options.label,
        attributes: {
          for: this.options.id || ''
        },
        i18nKey: labeli18nKey
      });
      container.appendChild(label);
    }

    // 创建输入框和滑块的容器
    const controlsContainer = this.ui.createElement({
      className: [
        'numeric-slider-controls',
        ...(this.options.controlsContainerClass ? [this.options.controlsContainerClass] : []),
      ],
    });

    // 创建数值输入框
    const inputContainer = this.ui.createElement({
      className: 'numeric-input-container',
    });

    const inputAttributes: Record<string, string> = {
      type: 'number',
      step: this.options.step?.toString() || '0.1',
      value: this.options.value?.toString() || '0',
      placeholder: this.options.placeholder || '',
      ...(this.options.disabled && { disabled: 'true' }),
      ...(this.options.readonly && { readonly: 'true' }),
      ...(this.options.id && { id: this.options.id })
    };

    // 只在 min/max 不为 null 时设置限制
    if (this.options.min !== null && this.options.min !== undefined) {
      inputAttributes.min = this.options.min.toString();
    }
    if (this.options.max !== null && this.options.max !== undefined) {
      inputAttributes.max = this.options.max.toString();
    }

    const input = this.ui.createElement({
      tag: 'input',
      className: [
        'form-control',
        ...(this.options.inputClass ? [this.options.inputClass] : []),
      ],
      attributes: inputAttributes,
    }) as HTMLInputElement;

    inputContainer.appendChild(input);
    controlsContainer.appendChild(inputContainer);

    // 创建自定义滑块
    const sliderContainer = this.ui.createElement({
      className: 'numeric-slider-wrapper'
    });

    // 创建滑轨容器
    const trackAttributes: Record<string, string> = {
      'data-step': this.options.step?.toString() || '0.1',
      'data-value': this.options.value?.toString() || '0'
    };

    // 只在 min/max 不为 null 时设置限制
    if (this.options.min !== null && this.options.min !== undefined) {
      trackAttributes['data-min'] = this.options.min.toString();
    }
    if (this.options.max !== null && this.options.max !== undefined) {
      trackAttributes['data-max'] = this.options.max.toString();
    }

    const trackContainer = this.ui.createElement({
      className: 'custom-slider-track',
      attributes: trackAttributes
    });

    // 左侧滑轨（已填充部分）
    const leftTrack = this.ui.createElement({
      className: 'custom-slider-left-track'
    });

    // 右侧滑轨（未填充部分）
    const rightTrack = this.ui.createElement({
      className: 'custom-slider-right-track'
    });

    // 滑块
    const slider = this.ui.createElement({
      className: [
        'custom-slider-thumb',
        ...(this.options.sliderClass ? [this.options.sliderClass] : []),
      ],
      attributes: {
        'data-value': this.options.value?.toString() || '0',
        ...(this.options.disabled && { 'data-disabled': 'true' })
      }
    });

    trackContainer.appendChild(leftTrack);
    trackContainer.appendChild(rightTrack);
    trackContainer.appendChild(slider);
    sliderContainer.appendChild(trackContainer);
    controlsContainer.appendChild(sliderContainer);

    container.appendChild(controlsContainer);

    // 创建值显示区域
    if (this.options.showValue) {
      const valueContainer = this.ui.createElement({
        className: [
          'numeric-slider-value',
          ...(this.options.valueClass ? [this.options.valueClass] : []),
        ],
      });

      const valueLabel = this.ui.createElement({
        tag: 'span',
        className: 'value-label',
        textContent: '当前值: '
      });

      const valueDisplay = this.ui.createElement({
        tag: 'span',
        className: 'value-display',
        textContent: this.options.valueFormat ?
          this.options.valueFormat(this.options.value || 0) :
          (this.options.value || 0).toString()
      });

      valueContainer.appendChild(valueLabel);
      valueContainer.appendChild(valueDisplay);
      container.appendChild(valueContainer);
    }

    return container;
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    // 输入框变化事件
    this.ui.on('change', this.inputElement, (e) => {
      if (this.isUpdating) return;
      const target = e.target as HTMLInputElement;
      const value = this.normalizeValue(target.value);
      this.updateValue(value);
    });

    // 自定义滑块拖拽事件
    this.ui.on('mousedown', this.sliderElement, (e) => {
      this.handleSliderMouseDown(e as MouseEvent);
    });

    this.ui.on('touchstart', this.sliderElement, (e) => {
      this.handleSliderTouchStart(e as TouchEvent);
    });

    // 滑轨点击事件
    this.ui.on('click', this.trackContainer, (e) => {
      this.handleTrackClick(e as MouseEvent);
    });

    // 键盘事件
    this.ui.on('keydown', this.inputElement, (e) => {
      this.handleKeyDown(e as KeyboardEvent);
    });

    this.ui.on('keyup', this.inputElement, (e) => {
      if (this.options.keyChange) {
        this.handleKeyUp(e as KeyboardEvent);
      }
    });

    // 焦点事件
    this.ui.on('focus', this.inputElement, (e) => {
      const currentValue = this.getValue();
      this.previousValue = currentValue === null ? 0 : currentValue;
    });

    // 鼠标滚轮事件 - 输入框和滑块区域都支持
    this.ui.on('wheel', this.inputElement, (e) => {
      this.handleWheel(e as WheelEvent);
    });

    this.ui.on('wheel', this.trackContainer, (e) => {
      this.handleWheel(e as WheelEvent);
    });

    // 全局鼠标和触摸事件
    document.addEventListener('mousemove', this.handleGlobalMouseMove.bind(this));
    document.addEventListener('mouseup', this.handleGlobalMouseUp.bind(this));
    document.addEventListener('touchmove', this.handleGlobalTouchMove.bind(this));
    document.addEventListener('touchend', this.handleGlobalTouchEnd.bind(this));
  }

  /**
   * 处理键盘按下事件
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (this.options.disabled || this.options.readonly) return;

    // 上下箭头键调整数值
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const direction = e.key === 'ArrowUp' ? 1 : -1;
      const step = e.shiftKey ? (this.options.step || 0.1) * 0.1 : (this.options.step || 0.1);
      const currentValue = this.getValue();
      const newValue = (currentValue === null ? 0 : currentValue) + step * direction;
      this.updateValue(newValue);
    }
    // Enter键失焦
    else if (e.key === 'Enter' && this.options.blurOnEnter) {
      this.inputElement.blur();
    }
    // Escape键恢复原值并失焦
    else if (e.key === 'Escape') {
      this.updateValue(this.previousValue);
      if (this.options.blurOnEscape) {
        this.inputElement.blur();
      }
    }
  }

  /**
   * 处理键盘抬起事件
   */
  private handleKeyUp(e: KeyboardEvent): void {
    if (e.key !== 'Escape') {
      const value = this.normalizeValue(this.inputElement.value);
      this.syncSliderFromInput(value);
    }
  }

  /**
   * 处理鼠标滚轮事件
   */
  private handleWheel(e: WheelEvent): void {
    if (this.options.disabled || this.options.readonly) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // deltaY > 0 表示向下滚动（减少值），deltaY < 0 表示向上滚动（增加值）
    const direction = e.deltaY < 0 ? 1 : -1;
    
    // Shift 键按住时使用更小的步长
    const step = e.shiftKey ? (this.options.step || 0.1) * 0.1 : (this.options.step || 0.1);
    
    const currentValue = this.getValue();
    const newValue = (currentValue === null ? 0 : currentValue) + step * direction;
    this.updateValue(newValue);
  }

  /**
   * 处理滑块鼠标按下事件
   */
  private handleSliderMouseDown(e: MouseEvent): void {
    if (this.options.disabled) return;
    e.preventDefault();
    this.isDragging = true;
    this.startX = e.clientX;
    this.startValue = this.getValue() || 0;
    this.sliderElement.style.cursor = 'grabbing';
  }

  /**
   * 处理滑块触摸开始事件
   */
  private handleSliderTouchStart(e: TouchEvent): void {
    if (this.options.disabled) return;
    e.preventDefault();
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.startValue = this.getValue() || 0;
  }

  /**
   * 处理滑轨点击事件
   */
  private handleTrackClick(e: MouseEvent): void {
    if (this.options.disabled) return;
    const min = this.getMin();
    const max = this.getMax();
    
    // 如果 min 和 max 都为 null，无法通过点击设置值
    if (min === null || max === null) {
      return;
    }
    
    const rect = this.trackContainer.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newValue = min + (max - min) * percentage;
    this.updateValue(newValue);
  }

  /**
   * 处理全局鼠标移动事件
   */
  private handleGlobalMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();
    this.updateSliderFromMouse(e.clientX);
  }

  /**
   * 处理全局鼠标抬起事件
   */
  private handleGlobalMouseUp(e: MouseEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.sliderElement.style.cursor = 'grab';
  }

  /**
   * 处理全局触摸移动事件
   */
  private handleGlobalTouchMove(e: TouchEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();
    this.updateSliderFromMouse(e.touches[0].clientX);
  }

  /**
   * 处理全局触摸结束事件
   */
  private handleGlobalTouchEnd(e: TouchEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;
  }

  /**
   * 根据鼠标位置更新滑块
   */
  private updateSliderFromMouse(clientX: number): void {
    const min = this.getMin();
    const max = this.getMax();
    
    // 如果 min 和 max 都为 null，使用相对变化
    if (min === null || max === null) {
      const rect = this.trackContainer.getBoundingClientRect();
      const deltaX = clientX - this.startX;
      const trackWidth = rect.width;
      // 使用一个参考范围来计算相对变化（不限制实际值）
      const referenceRange = 100; // 参考范围
      const deltaPercentage = deltaX / trackWidth;
      const deltaValue = referenceRange * deltaPercentage;
      const step = this.getStep();
      const newValue = this.startValue + deltaValue;
      const steppedValue = Math.round(newValue / step) * step;
      this.updateValue(steppedValue);
      return;
    }
    
    const rect = this.trackContainer.getBoundingClientRect();
    const deltaX = clientX - this.startX;
    const trackWidth = rect.width;
    const deltaPercentage = deltaX / trackWidth;
    const step = this.getStep();
    const range = max - min;
    const deltaValue = range * deltaPercentage;
    const newValue = this.startValue + deltaValue;
    
    // 应用步长
    const steppedValue = Math.round(newValue / step) * step;
    this.updateValue(steppedValue);
  }

  /**
   * 从输入框同步到滑块
   */
  private syncSliderFromInput(value: number | null): void {
    this.isUpdating = true;
    const normalizedValue = this.normalizeValue(value);
    this.updateSliderPosition(normalizedValue);
    this.updateValueDisplay(normalizedValue);
    this.isUpdating = false;
  }

  /**
   * 从滑块同步到输入框
   */
  private updateValue(value: number | null): void {
    this.isUpdating = true;
    const normalizedValue = this.normalizeValue(value);
    this.inputElement.value = normalizedValue === null ? '' : normalizedValue.toString();
    this.updateSliderPosition(normalizedValue);
    this.updateValueDisplay(normalizedValue);
    this.notifyChange(normalizedValue);
    this.isUpdating = false;
  }

  /**
   * 更新滑块位置和滑轨长度
   */
  private updateSliderPosition(value: number | null): void {
    if (value === null) return;
    
    const min = this.getMin();
    const max = this.getMax();
    
    // 计算显示范围：如果 min/max 为 null，使用参考范围来显示位置（但不限制实际值）
    const displayMin = min !== null ? min : -100;
    const displayMax = max !== null ? max : 100;
    const percentage = Math.max(0, Math.min(1, (value - displayMin) / (displayMax - displayMin)));
    
    // 更新滑块位置
    this.sliderElement.style.left = `${percentage * 100}%`;
    
    // 更新滑轨长度
    this.leftTrack.style.width = `${percentage * 100}%`;
    this.rightTrack.style.width = `${(1 - percentage) * 100}%`;
    
    // 更新数据属性
    this.trackContainer.setAttribute('data-value', value.toString());
    this.sliderElement.setAttribute('data-value', value.toString());
  }

  /**
   * 标准化数值
   */
  private normalizeValue(value: any): number | null {
    try {
      if (typeof value === 'string') {
        // 检查空值
        if (value === '' || value === null || value === undefined) {
          return this.options.allowNull ? null : 0;
        }

        // 替换逗号为点号（国际化键盘支持）
        value = value.replace(/,/g, '.');

        // 移除空格
        value = value.replace(/\s/g, '');

        // 解析数值
        value = parseFloat(value);
      }

      if (isNaN(value)) {
        return this.options.allowNull ? null : 0;
      }

      // 限制在最小最大值范围内（只在 min/max 不为 null 时限制）
      if (this.options.min !== null && this.options.min !== undefined) {
        value = Math.max(this.options.min, value);
      }
      if (this.options.max !== null && this.options.max !== undefined) {
        value = Math.min(this.options.max, value);
      }

      // 应用精度
      if (this.options.precision !== null && this.options.precision !== undefined) {
        if (this.options.precision < 0) {
          value = parseFloat(value);
        } else {
          value = parseFloat(value.toFixed(this.options.precision));
        }
      }

      return value;
    } catch (error) {
      return this.options.allowNull ? null : 0;
    }
  }


  /**
   * 更新值显示
   */
  private updateValueDisplay(value: number | null): void {
    if (this.valueDisplayElement) {
      if (value === null) {
        this.valueDisplayElement.textContent = 'null';
      } else if (this.options.valueFormat) {
        this.valueDisplayElement.textContent = this.options.valueFormat(value);
      } else {
        this.valueDisplayElement.textContent = value.toString();
      }
    }
  }

  /**
   * 通知值变化
   */
  private notifyChange(value: number | null): void {
    this.onChangeCallbacks.forEach(callback => {
      try {
        callback(value);
      } catch (error) {
        console.error('数值滑块变化回调执行错误:', error);
      }
    });
  }

  /**
   * 获取当前值
   */
  getValue(): number | null {
    return this.normalizeValue(this.inputElement.value);
  }

  /**
   * 更新当前值显示
   */
  public setValue(value: number | null): void {
    this.isUpdating = true;
    const normalizedValue = this.normalizeValue(value);
    this.inputElement.value = normalizedValue === null ? '' : normalizedValue.toString();
    this.updateSliderPosition(normalizedValue);
    this.updateValueDisplay(normalizedValue);
    this.isUpdating = false;
  }

  /**
   * 设置当前值并同步
   */
  syncFromInput(value: number | null): void {
    const normalizedValue = this.normalizeValue(value);
    this.isUpdating = true;
    this.inputElement.value = normalizedValue === null ? '' : normalizedValue.toString();
    this.updateSliderPosition(normalizedValue);
    this.updateValueDisplay(normalizedValue);
    this.notifyChange(normalizedValue);
    this.isUpdating = false;
  }

  /**
   * 获取最小值，null 表示不限制
   */
  getMin(): number | null {
    return this.options.min ?? null;
  }

  /**
   * 设置最小值，null 表示不限制
   */
  setMin(min: number | null): void {
    this.options.min = min;
    if (min !== null && min !== undefined) {
      this.inputElement.min = min.toString();
      this.trackContainer.setAttribute('data-min', min.toString());
    } else {
      this.inputElement.removeAttribute('min');
      this.trackContainer.removeAttribute('data-min');
    }
    this.updateSliderPosition(this.getValue());
  }

  /**
   * 获取最大值，null 表示不限制
   */
  getMax(): number | null {
    return this.options.max ?? null;
  }

  /**
   * 设置最大值，null 表示不限制
   */
  setMax(max: number | null): void {
    this.options.max = max;
    if (max !== null && max !== undefined) {
      this.inputElement.max = max.toString();
      this.trackContainer.setAttribute('data-max', max.toString());
    } else {
      this.inputElement.removeAttribute('max');
      this.trackContainer.removeAttribute('data-max');
    }
    this.updateSliderPosition(this.getValue());
  }

  /**
   * 获取步长
   */
  getStep(): number {
    return this.options.step || 0.1;
  }

  /**
   * 设置步长
   */
  setStep(step: number): void {
    this.options.step = step;
    this.inputElement.step = step.toString();
    this.trackContainer.setAttribute('data-step', step.toString());
  }

  /**
   * 获取精度
   */
  getPrecision(): number {
    return this.options.precision || 2;
  }

  /**
   * 设置精度
   */
  setPrecision(precision: number): void {
    this.options.precision = precision;
  }

  /**
   * 是否禁用
   */
  isDisabled(): boolean {
    return this.inputElement.disabled;
  }

  /**
   * 设置禁用状态
   */
  setDisabled(disabled: boolean): void {
    this.inputElement.disabled = disabled;
    this.sliderElement.setAttribute('data-disabled', disabled.toString());
    this.leftTrack.setAttribute('data-disabled', disabled.toString());
    // this.rightTrack.setAttribute('data-disabled', disabled.toString());
    this.options.disabled = disabled;
  }

  /**
   * 设置组件激活状态（启用/禁用整个组件）
   */
  setActive(status: boolean): void {
    if (!status) {
      // 禁用整个组件
      this.container.classList.add('forbidden-event-dom');
      
      // 禁用输入框和滑块
      this.setDisabled(true);
    } else {
      // 启用整个组件
      this.container.classList.remove('forbidden-event-dom');
      
      // 启用输入框和滑块
      this.setDisabled(false);
    }
  }

  /**
   * 是否只读
   */
  isReadOnly(): boolean {
    return this.inputElement.readOnly;
  }

  /**
   * 设置只读状态
   */
  setReadOnly(readonly: boolean): void {
    this.inputElement.readOnly = readonly;
    this.options.readonly = readonly;
  }

  /**
   * 添加值变化监听器
   */
  onChange(callback: (value: number | null) => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * 移除值变化监听器
   */
  offChange(callback: (value: number | null) => void): void {
    const index = this.onChangeCallbacks.indexOf(callback);
    if (index > -1) {
      this.onChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * 获取输入框元素
   */
  getInputElement(): HTMLInputElement {
    return this.inputElement;
  }

  /**
   * 获取滑块元素
   */
  getSliderElement(): HTMLElement {
    return this.sliderElement;
  }

  /**
   * 获取容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 聚焦输入框
   */
  focus(): void {
    this.inputElement.focus();
  }

  /**
   * 失焦输入框
   */
  blur(): void {
    this.inputElement.blur();
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.onChangeCallbacks = [];

    // 移除事件监听器
    this.ui.off('input', this.inputElement, () => { });
    this.ui.off('change', this.inputElement, () => { });
    this.ui.off('keydown', this.inputElement, () => { });
    this.ui.off('keyup', this.inputElement, () => { });
    this.ui.off('focus', this.inputElement, () => { });
    this.ui.off('wheel', this.inputElement, () => { });
    this.ui.off('wheel', this.trackContainer, () => { });

    this.ui.off('mousedown', this.sliderElement, () => { });
    this.ui.off('touchstart', this.sliderElement, () => { });
    this.ui.off('click', this.trackContainer, () => { });

    // 移除全局事件监听器
    document.removeEventListener('mousemove', this.handleGlobalMouseMove.bind(this));
    document.removeEventListener('mouseup', this.handleGlobalMouseUp.bind(this));
    document.removeEventListener('touchmove', this.handleGlobalTouchMove.bind(this));
    document.removeEventListener('touchend', this.handleGlobalTouchEnd.bind(this));

    // 移除DOM元素
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// 导出默认实例创建函数
export function createNumericSliderUI(options: NumericSliderOptions = {}): NumericSliderUI {
  return new NumericSliderUI(options);
}
